/**
 * Selection Behavior
 *
 * Handles node and edge selection:
 * - Click to select (replace)
 * - Shift+click to toggle
 * - Ctrl+click to select node + descendants
 * - Ctrl+Shift+click to toggle node + descendants
 * - Click empty to clear
 * - Box selection (drag on empty)
 * - Mod+A to select all
 * - Escape to clear
 */

import type { BehaviorContext } from './types';
import type { SelectionMode } from '../types';
import * as viewState from '../view-state';
import { getDescendants } from '../graph';

/**
 * Register selection behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerSelectionBehavior(ctx: BehaviorContext): () => void {
	const {
		input,
		getViewState,
		updateViewState,
		hitTest,
		getNodes,
		getEdges,
		screenToWorld,
		log,
	} = ctx;
	const unsubscribers: (() => void)[] = [];

	// =========================================================================
	// Click to Select (nodes and edges)
	// =========================================================================

	// Note: This handler only handles SELECTION. Drag initiation is in drag.ts.
	// We don't call stopPropagation so drag behavior can also respond.
	unsubscribers.push(
		input.on('mousedown', { mode: 'default' }, (inputCtx) => {
			// Right-click: select node under cursor for context menu (if not already selected)
			if (inputCtx.button === 2) {
				const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
				const hit = hitTest(point);
				if (hit.nodeId) {
					const state = getViewState();
					// Only change selection if the node isn't already selected
					// (preserves multi-selection when right-clicking a selected node)
					if (!state.selection.has(hit.nodeId)) {
						updateViewState(
							viewState.applySelection(state, hit.nodeId, 'replace'),
						);
					}
				}
				return;
			}

			// Only handle left mouse button (ignore middle for panning)
			if (inputCtx.button !== 0) return;

			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			const hit = hitTest(point);
			const state = getViewState();

			// Determine selection mode from modifiers
			// Shift = toggle, otherwise replace
			const mode: SelectionMode = inputCtx.modifiers.shift
				? 'toggle'
				: 'replace';

			if (hit.nodeId) {
				// Ctrl+click: select node and all descendants
				if (inputCtx.modifiers.ctrl && !inputCtx.modifiers.alt) {
					const edges = getEdges();
					const descendants = getDescendants(edges, hit.nodeId);
					const ids = [hit.nodeId, ...descendants];
					const ctrlMode: SelectionMode = inputCtx.modifiers.shift
						? 'toggle'
						: 'replace';
					updateViewState(viewState.applyMultiSelection(state, ids, ctrlMode));
					return;
				}

				const wasSelected = state.selection.has(hit.nodeId);
				const isCopyDrag = inputCtx.modifiers.alt && wasSelected;

				// Don't change selection if Alt+click on selected node (copy-drag preserves selection)
				if (!isCopyDrag) {
					// For replace mode on already-selected node, don't change selection
					// (allows dragging multi-selection by clicking any selected node)
					if (mode === 'replace' && wasSelected && state.selection.size > 1) {
						// Keep current selection
					} else {
						updateViewState(viewState.applySelection(state, hit.nodeId, mode));
					}
				}
				return;
			}

			if (hit.edgeId) {
				updateViewState(viewState.applySelection(state, hit.edgeId, mode));
				log?.('edge-select', hit.edgeId);
				return;
			}

			// Click on empty - clear selection and start box select
			// BUT NOT if hovering a resize handle (resize behavior will handle it)
			if (state.hovered.resizeHandleId) {
				return;
			}
			updateViewState(viewState.clearSelection(state));
			updateViewState(viewState.startBoxSelect(getViewState(), point));
			input.pushMode('box-select');
			log?.(
				'box-select-start',
				`at (${point.x.toFixed(0)}, ${point.y.toFixed(0)})`,
			);
		}),
	);

	// =========================================================================
	// Box Select Mode
	// =========================================================================

	unsubscribers.push(
		input.on('mousemove', { mode: 'box-select' }, (inputCtx) => {
			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			updateViewState(viewState.updateBoxSelect(getViewState(), point));
		}),
	);

	unsubscribers.push(
		input.on('mouseup', { mode: 'box-select' }, () => {
			const state = getViewState();
			const rect = viewState.getBoxSelectRect(state);

			if (rect) {
				const nodes = getNodes();
				const edges = getEdges();
				const editorNodes = nodes.map((n) => ({
					id: n.id,
					position: n.position,
					size: n.size,
				}));
				const editorEdges = edges.map((e) => ({
					id: e.id,
					sourceNodeId: e.sourceNodeId,
					targetNodeId: e.targetNodeId,
				}));
				const nodesInBox = viewState.getNodesInRect(editorNodes, rect);
				const edgesInBox = viewState.getEdgesInRect(
					editorEdges,
					editorNodes,
					rect,
				);
				const allInBox = [...nodesInBox, ...edgesInBox];
				log?.(
					'box-select-end',
					`${nodesInBox.length} nodes, ${edgesInBox.length} edges selected`,
				);
				updateViewState(viewState.finishBoxSelect(state, allInBox, 'replace'));
			} else {
				updateViewState(viewState.cancelBoxSelect(state));
			}

			input.popMode('box-select');
		}),
	);

	unsubscribers.push(
		input.on(
			'keydown',
			'escape',
			{
				mode: 'box-select',
				command: { id: 'box-select.cancel', name: 'Cancel Box Select' },
			},
			() => {
				log?.('box-select-cancel', '');
				updateViewState(viewState.cancelBoxSelect(getViewState()));
				input.popMode('box-select');
			},
		),
	);

	// =========================================================================
	// Global Selection Shortcuts
	// =========================================================================

	// Select All (Mod+A)
	unsubscribers.push(
		input.on(
			'keydown',
			'mod+a',
			{
				mode: 'default',
				command: { id: 'selection.select-all', name: 'Select All' },
				category: 'Selection',
			},
			(inputCtx) => {
				inputCtx.stopPropagation();
				const nodes = getNodes();
				const edges = getEdges();
				const nodeIds = nodes.map((n) => n.id);
				const edgeIds = edges.map((e) => e.id);
				const allIds = [...nodeIds, ...edgeIds];
				log?.('select-all', `${nodeIds.length} nodes, ${edgeIds.length} edges`);
				updateViewState(viewState.selectAll(getViewState(), allIds));
			},
		),
	);

	// Clear Selection (Escape)
	unsubscribers.push(
		input.on(
			'keydown',
			'escape',
			{
				mode: 'default',
				command: { id: 'selection.clear', name: 'Clear Selection' },
				category: 'Selection',
			},
			() => {
				const state = getViewState();
				if (state.selection.size > 0) {
					log?.('clear-selection', '');
					updateViewState(viewState.clearSelection(state));
				}
			},
		),
	);

	// =========================================================================
	// Cleanup
	// =========================================================================

	return () => {
		for (const unsub of unsubscribers) {
			unsub();
		}
	};
}
